/**
 * Port de assets/lens-selection.js (`_getGiftRuleId` a
 * `_pushManagedGiftToItems`, ~linhas 175-414, protegido) — mesma chave de
 * sessionStorage (`_zrzs_cart_gift_rules`) do fluxo legado. Sem upsell
 * (fora de escopo).
 */

const CART_GIFT_RULES_STORAGE_KEY = '_zrzs_cart_gift_rules';

/**
 * @param {any} gift
 * @returns {string}
 */
function getGiftRuleId(gift) {
  const id = gift?.gift_id ?? gift?.giftId;
  return id != null && id !== '' ? String(id) : '';
}

/**
 * @param {any} gift
 * @returns {string}
 */
function getGiftRuleKey(gift) {
  const ruleId = getGiftRuleId(gift);
  return ruleId || `variant:${gift.variant_id}`;
}

/**
 * @param {any[]} cartItems
 * @returns {Set<string>}
 */
function getManagedGiftRuleIdsInCart(cartItems) {
  const ruleIds = new Set();
  for (const item of cartItems) {
    if (
      item.properties?._isGift !== 'true' ||
      item.properties?._stackGifts !== 'false'
    ) {
      continue;
    }
    const ruleId = item.properties?._giftRuleId;
    ruleIds.add(ruleId ? String(ruleId) : `variant:${item.variant_id}`);
  }
  return ruleIds;
}

/** @returns {Array<{ sourceKey: string, gift: any }>} */
function readCartGiftRules() {
  try {
    const raw = sessionStorage.getItem(CART_GIFT_RULES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** @param {Array<{ sourceKey: string, gift: any }>} entries */
function writeCartGiftRules(entries) {
  try {
    if (!entries.length) {
      sessionStorage.removeItem(CART_GIFT_RULES_STORAGE_KEY);
    } else {
      sessionStorage.setItem(
        CART_GIFT_RULES_STORAGE_KEY,
        JSON.stringify(entries)
      );
    }
  } catch {
    // sessionStorage indisponível (modo privado etc.) — segue sem persistir.
  }
}

/**
 * @param {string} sourceKey
 * @param {any[]} gifts
 */
function registerCartGiftRules(sourceKey, gifts) {
  const managed = (gifts || []).filter(
    g => g.stack_gifts === false && g.variant_id
  );
  const entries = readCartGiftRules().filter(e => e.sourceKey !== sourceKey);
  for (const gift of managed) entries.push({ sourceKey, gift });
  writeCartGiftRules(entries);
}

/**
 * @param {any[]} cartItems
 * @param {Set<string>} [excludeKeys] itens sendo removidos agora — contam no snapshot mas não como fonte ativa.
 */
function pruneCartGiftRules(cartItems, excludeKeys = new Set()) {
  const activeSources = new Set();
  for (const item of cartItems) {
    if (excludeKeys.has(item.key)) continue;
    if (item.properties?._isGift === 'true') continue;
    const sourceKey = item.properties?._productVariantTitleValue;
    if (sourceKey) activeSources.add(sourceKey);
  }
  const entries = readCartGiftRules().filter(e =>
    activeSources.has(e.sourceKey)
  );
  writeCartGiftRules(entries);
}

/**
 * @param {any[]} cartItems
 * @param {string[]} keysToRemove
 */
function removeCartGiftRulesForCartKeys(cartItems, keysToRemove) {
  const sources = new Set();
  for (const item of cartItems) {
    if (!keysToRemove.includes(item.key)) continue;
    const sourceKey = item.properties?._productVariantTitleValue;
    if (sourceKey) sources.add(sourceKey);
  }
  if (!sources.size) return;
  const entries = readCartGiftRules().filter(e => !sources.has(e.sourceKey));
  writeCartGiftRules(entries);
}

/** @returns {any[]} */
function getUniqueManagedGiftsFromStorage() {
  const byKey = new Map();
  for (const { gift } of readCartGiftRules()) {
    if (!gift?.variant_id || gift.stack_gifts !== false) continue;
    const key = getGiftRuleKey(gift);
    if (!byKey.has(key)) byKey.set(key, gift);
  }
  return [...byKey.values()];
}

/**
 * @param {any} gift
 * @param {number} total
 * @returns {boolean}
 */
function isGiftInValueBand(gift, total) {
  if (gift.min_cart_value != null && total < gift.min_cart_value) return false;
  if (gift.max_cart_value != null && total > gift.max_cart_value) return false;
  return true;
}

/**
 * @param {any} gift
 * @param {Date} now
 * @returns {boolean}
 */
function isGiftScheduledNow(gift, now) {
  if (gift.schedule !== 'Definir um prazo de atividade') return true;
  if (!gift.start || !gift.end) return true;
  const startDate = new Date(gift.start);
  const endDate = new Date(gift.end);
  return now >= startDate && now <= endDate;
}

/**
 * @param {number} projectedTotal
 * @param {any[]} cartItems
 * @returns {{ toRemove: string[], toAdd: any[] }}
 */
function syncManagedGifts(projectedTotal, cartItems) {
  const rules = getUniqueManagedGiftsFromStorage();
  if (!rules.length) return { toRemove: [], toAdd: [] };

  const rulesByKey = new Map(rules.map(gift => [getGiftRuleKey(gift), gift]));

  const managedInCart = cartItems.filter(
    item =>
      item.properties?._isGift === 'true' &&
      item.properties?._stackGifts === 'false'
  );

  const toRemove = managedInCart
    .filter(item => {
      const key = item.properties?._giftRuleId || `variant:${item.variant_id}`;
      const gift = rulesByKey.get(String(key));
      if (!gift) return true;
      return !isGiftInValueBand(gift, projectedTotal);
    })
    .map(item => item.key);

  const inCartKeys = getManagedGiftRuleIdsInCart(managedInCart);

  const toAdd = rules.filter(gift => {
    if (!isGiftInValueBand(gift, projectedTotal)) return false;
    return !inCartKeys.has(getGiftRuleKey(gift));
  });

  return { toRemove, toAdd };
}

/**
 * @param {any} gift
 * @param {Array<{id: string, quantity: number, properties: Record<string, string>}>} giftItems
 * @param {Set<string>} cartGiftRuleIds
 */
function pushManagedGiftToItems(gift, giftItems, cartGiftRuleIds) {
  const giftVariantId = String(gift.variant_id);
  const ruleId = getGiftRuleId(gift);
  if (ruleId) {
    if (cartGiftRuleIds.has(ruleId)) return;
  } else if (cartGiftRuleIds.has(`variant:${giftVariantId}`)) {
    return;
  }

  /** @type {Record<string, string>} */
  const properties = {
    _isGift: 'true',
    _giftVariantId: giftVariantId,
    _stackGifts: 'false',
    _minCartValue:
      gift.min_cart_value != null ? String(gift.min_cart_value) : 'none',
    _maxCartValue:
      gift.max_cart_value != null ? String(gift.max_cart_value) : 'none',
  };
  if (ruleId) properties._giftRuleId = ruleId;

  giftItems.push({ id: giftVariantId, quantity: 1, properties });
  cartGiftRuleIds.add(ruleId || `variant:${giftVariantId}`);
}

/** @returns {Promise<{ cartTotal: number, items: any[] } | null>} null = falha real (distinto de carrinho vazio de verdade). */
async function fetchCartSnapshot() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return null;
    const cart = await res.json();
    return { cartTotal: cart.total_price || 0, items: cart.items || [] };
  } catch {
    return null;
  }
}

/**
 * Port de assets/lens-selection.js#addToCart (parte de brindes, ~linhas
 * 2090-2172): prefetch do carrinho real, poda de regras órfãs, registro de
 * novas regras gerenciadas, sincroniza por faixa de valor e monta os itens
 * de brinde desta compra (gerenciado = elegibilidade marcada na armação;
 * empilhável = item próprio ligado à armação via `_father`).
 * @param {object} params
 * @param {any[] | null} [params.giftsData]
 * @param {string} params.frameSelfMarker `${frameVariantTitle} - ${frameVariantId}`.
 * @param {number} params.pendingAdditionsTotal preço (centavos) do que esta chamada vai adicionar (armação+lente).
 * @returns {Promise<{ giftItems: Array<{id: string, quantity: number, properties: Record<string, string>}>, frameGiftProperties: Record<string, string> }>}
 */
export async function buildGiftItems({
  giftsData,
  frameSelfMarker,
  pendingAdditionsTotal,
}) {
  /** @type {Array<{id: string, quantity: number, properties: Record<string, string>}>} */
  const giftItems = [];
  /** @type {Record<string, string>} */
  const frameGiftProperties = {};

  const hasGiftsData = Array.isArray(giftsData) && giftsData.length > 0;
  const managedOnProduct = (giftsData || []).filter(
    g => g.stack_gifts === false && g.variant_id
  );
  const hasStoredRules = readCartGiftRules().length > 0;

  if (!hasGiftsData && !hasStoredRules) {
    return { giftItems, frameGiftProperties };
  }

  const now = new Date();
  const cartSnapshot = await fetchCartSnapshot();

  // Sem leitura real do carrinho, não dá pra confiar em nenhuma decisão de
  // faixa/elegibilidade baseada nele — melhor não mexer nas regras
  // gerenciadas nesta passada do que tratar a falha como "carrinho vazio"
  // (podaria regras válidas e recalcularia elegibilidade errada).
  if (cartSnapshot && (managedOnProduct.length || hasStoredRules)) {
    const { cartTotal, items: cartItems } = cartSnapshot;
    pruneCartGiftRules(cartItems);
    if (hasGiftsData && managedOnProduct.length) {
      registerCartGiftRules(frameSelfMarker, giftsData);
    }

    const projectedTotal = cartTotal + pendingAdditionsTotal;
    const { toRemove, toAdd } = syncManagedGifts(projectedTotal, cartItems);
    const cartGiftRuleIds = getManagedGiftRuleIdsInCart(cartItems);

    if (toRemove.length > 0) {
      /** @type {Record<string, number>} */
      const updates = {};
      toRemove.forEach(key => {
        updates[key] = 0;
      });
      const removeResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      // Só assume removido de verdade se o /cart/update.js confirmou — senão
      // o registro local ficava inconsistente com o carrinho real.
      if (removeResponse.ok) {
        cartItems
          .filter(i => toRemove.includes(i.key))
          .forEach(i => {
            const ruleId = i.properties?._giftRuleId;
            if (ruleId) cartGiftRuleIds.delete(String(ruleId));
            else cartGiftRuleIds.delete(`variant:${i.variant_id}`);
          });
      }
    }

    for (const gift of toAdd) {
      if (!isGiftScheduledNow(gift, now)) continue;
      pushManagedGiftToItems(gift, giftItems, cartGiftRuleIds);
    }
  }

  if (hasGiftsData) {
    for (const gift of giftsData) {
      if (!isGiftScheduledNow(gift, now)) continue;
      if (!gift.variant_id) continue;

      if (gift.stack_gifts === false) {
        frameGiftProperties[`_eligibleForGift_${getGiftRuleKey(gift)}`] =
          'true';
      } else {
        /** @type {Record<string, string>} */
        const giftProperties = {
          _isGift: 'true',
          _father: frameSelfMarker,
        };
        if (gift.rule_works_in_reverse === true) {
          giftProperties._removeWithFather = 'true';
        }
        giftItems.push({
          id: gift.variant_id.toString(),
          quantity: 1,
          properties: giftProperties,
        });
      }
    }
  }

  return { giftItems, frameGiftProperties };
}

/**
 * Port de assets/cart-remove-all.js#handleRemoveAll (sincroniza remoção de
 * brindes) — sem isso, remover a armação deixa o brinde órfão no carrinho.
 * @param {object} params
 * @param {any[]} params.cartItems snapshot do carrinho antes da remoção (this.lastCartItems).
 * @param {string[]} params.keysToRemove chaves já decididas (armação + grupo).
 * @param {number} params.cartTotalCents cart.total_price antes da remoção.
 * @returns {{ keysToRemove: string[], giftsToAdd: Array<{id: string, quantity: number, properties: Record<string, string>}> }}
 */
export function syncGiftsOnRemove({ cartItems, keysToRemove, cartTotalCents }) {
  const removing = [...keysToRemove];

  // 1) Empilhável (_removeWithFather): sai junto com a armação-pai removida.
  const removedFrameMarkers = new Set(
    cartItems
      .filter(item => removing.includes(item.key))
      .map(item => item.properties?._productVariantTitleValue)
      .filter(Boolean)
  );
  cartItems.forEach(item => {
    const props = item.properties || {};
    if (
      !removing.includes(item.key) &&
      props._isGift === 'true' &&
      props._removeWithFather === 'true' &&
      removedFrameMarkers.has(props._father)
    ) {
      removing.push(item.key);
    }
  });

  // 2) Gerenciado: só sai se nenhum outro item no carrinho ainda for elegível.
  const removedNowSet = new Set(removing);
  const eligibleRuleKeys = new Set();
  cartItems
    .filter(item => removedNowSet.has(item.key))
    .forEach(item => {
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (key.startsWith('_eligibleForGift_') && value === 'true') {
          eligibleRuleKeys.add(key.slice('_eligibleForGift_'.length));
        }
      });
    });
  eligibleRuleKeys.forEach(ruleKey => {
    const eligibleProp = `_eligibleForGift_${ruleKey}`;
    const hasOtherEligible = cartItems.some(
      item =>
        !removedNowSet.has(item.key) &&
        item.properties?.[eligibleProp] === 'true'
    );
    if (hasOtherEligible) return;
    cartItems.forEach(item => {
      const props = item.properties || {};
      const giftRuleKey = props._giftRuleId || props._giftVariantId;
      if (
        props._isGift === 'true' &&
        props._stackGifts === 'false' &&
        giftRuleKey === ruleKey &&
        !removedNowSet.has(item.key)
      ) {
        removing.push(item.key);
      }
    });
  });

  // 3) Ressincroniza brindes gerenciados por faixa de valor (regras no sessionStorage).
  removeCartGiftRulesForCartKeys(cartItems, removing);
  const removingKeySet = new Set(removing);
  pruneCartGiftRules(cartItems, removingKeySet);

  const rules = getUniqueManagedGiftsFromStorage();
  if (!rules.length) return { keysToRemove: removing, giftsToAdd: [] };

  const removedTotal = cartItems
    .filter(item => removingKeySet.has(item.key))
    .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
  const projectedTotal = Math.max(0, cartTotalCents - removedTotal);
  const rulesByKey = new Map(rules.map(gift => [getGiftRuleKey(gift), gift]));

  const managedInCart = cartItems.filter(
    item =>
      item.properties?._isGift === 'true' &&
      item.properties?._stackGifts === 'false' &&
      !removingKeySet.has(item.key)
  );

  managedInCart
    .filter(item => {
      const key = item.properties?._giftRuleId || `variant:${item.variant_id}`;
      const gift = rulesByKey.get(String(key));
      return gift ? !isGiftInValueBand(gift, projectedTotal) : true;
    })
    .forEach(item => removing.push(item.key));

  const finalRemovingSet = new Set(removing);
  const remainingNonGiftItems = cartItems.filter(
    item =>
      !finalRemovingSet.has(item.key) && item.properties?._isGift !== 'true'
  );

  // Carrinho só com brinde(s): remove tudo e descarta as regras (comportamento do legado).
  if (remainingNonGiftItems.length === 0) {
    managedInCart.forEach(item => removing.push(item.key));
    writeCartGiftRules([]);
    return { keysToRemove: removing, giftsToAdd: [] };
  }

  const inCartKeys = new Set();
  // Regras saindo nesta chamada — nunca re-adiciona abaixo, mesmo que a
  // elegibilidade ainda seja satisfeita (senão o próprio brinde removido
  // voltava imediatamente).
  const removedManagedGiftRuleKeys = new Set();
  cartItems.forEach(item => {
    const props = item.properties || {};
    if (props._isGift !== 'true' || props._stackGifts !== 'false') return;
    const key = props._giftRuleId
      ? String(props._giftRuleId)
      : `variant:${item.variant_id}`;
    if (finalRemovingSet.has(item.key)) {
      removedManagedGiftRuleKeys.add(key);
    } else {
      inCartKeys.add(key);
    }
  });

  const giftsToAdd = rules
    .filter(gift => {
      const key = getGiftRuleKey(gift);
      return (
        isGiftInValueBand(gift, projectedTotal) &&
        !inCartKeys.has(key) &&
        !removedManagedGiftRuleKeys.has(key)
      );
    })
    .map(gift => {
      const ruleId = getGiftRuleId(gift);
      /** @type {Record<string, string>} */
      const properties = {
        _isGift: 'true',
        _giftVariantId: String(gift.variant_id),
        _stackGifts: 'false',
        _minCartValue:
          gift.min_cart_value != null ? String(gift.min_cart_value) : 'none',
        _maxCartValue:
          gift.max_cart_value != null ? String(gift.max_cart_value) : 'none',
      };
      if (ruleId) properties._giftRuleId = ruleId;
      return { id: String(gift.variant_id), quantity: 1, properties };
    });

  return { keysToRemove: removing, giftsToAdd };
}
